import axios from 'axios';

import {
  beaconBackendUrl,
  formApiEnabled,
  formBackendUrl,
  formPaths,
  partialBackendUrl,
} from '../../site-config';

/**
 * Lead submission with retry, an on-device retry queue, a page-hide beacon
 * and progressive capture of the values typed so far.
 *
 * Ported from optimawindowsdoors.com (2026-08-24), where a real lead was lost
 * when the POST to the submissions API died with no response inside a
 * Facebook in-app browser. Every failure now (1) retries, (2) is queued in
 * localStorage and re-sent automatically, and (3) the values typed so far
 * have already reached the API through POST /partial, where the API emails a
 * digest of contact details that never completed.
 *
 * This site has no Sentry DSN, so unlike the Optima version nothing is
 * reported to Sentry; the partial-lead digest is the safety net.
 */

const PENDING_KEY = 'pendingLeadSubmission';
const MAX_PENDING_AGE_MS = 7 * 24 * 60 * 60 * 1000;
// 3 fast attempts while the visitor waits, then 7 more in the background
// while the page stays open (10 total), then the queue persists across visits.
const RETRY_DELAYS_MS = [1000, 3000];
const BACKGROUND_RETRY_DELAYS_MS = [
  10000, 20000, 30000, 45000, 60000, 60000, 60000,
];
const MAX_TOTAL_ATTEMPTS = 10;
const REQUEST_TIMEOUT_MS = 20000;

function sleep(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

function isRetryable(error) {
  // No response at all: network error, CORS/edge block, timeout.
  if (!error || !error.response) return true;
  const { status } = error.response;
  return status >= 500 || status === 408 || status === 429;
}

function postOnce(payload) {
  return axios.post(formBackendUrl, payload, {
    headers: {
      Authorization: `Bearer ${process.env.NEXT_PUBLIC_API_TOKEN}`,
    },
    timeout: REQUEST_TIMEOUT_MS,
  });
}

function readPending() {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function writePending(record) {
  try {
    window.localStorage.setItem(PENDING_KEY, JSON.stringify(record));
    return true;
  } catch (e) {
    return false;
  }
}

function clearPending() {
  try {
    window.localStorage.removeItem(PENDING_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * Give this browser a fresh session uuid after a confirmed submission, so a
 * later request from the same browser is not treated as a retry of this one
 * (the API is idempotent per sessionUuid for 24h).
 */
export function rotateSessionUuid(usedUuid) {
  try {
    if (
      window.crypto &&
      window.crypto.randomUUID &&
      window.localStorage.getItem('sessionUuid') === usedUuid
    ) {
      window.localStorage.setItem('sessionUuid', window.crypto.randomUUID());
    }
  } catch (e) {
    // ignore
  }
}

/**
 * Submit a lead. Retries transient failures, and on final failure queues the
 * payload on-device for automatic re-send, then rethrows so the form can
 * show its error state.
 */
export async function submitLead(payload) {
  // API switched off (see site-config.js): show the developer exactly what
  // would have been posted, then resolve as a success so the thank-you flow
  // stays testable without touching the customer's lead pipeline.
  if (!formApiEnabled) {
    // eslint-disable-next-line no-console
    console.info(
      '[submit-lead] API disabled, nothing posted. Payload:',
      payload,
    );
    return { data: { status: 'success', phpsessid: 'local-dev-no-api' } };
  }

  let attempt = 0;
  let lastError;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    attempt += 1;
    try {
      // eslint-disable-next-line no-await-in-loop
      const response = await postOnce(payload);
      rotateSessionUuid(payload.sessionUuid);
      return response;
    } catch (error) {
      lastError = error;
      if (!isRetryable(error) || attempt > RETRY_DELAYS_MS.length) break;
      // eslint-disable-next-line no-await-in-loop
      await sleep(RETRY_DELAYS_MS[attempt - 1]);
    }
  }

  const queued =
    isRetryable(lastError) &&
    writePending({ payload, savedAt: Date.now(), attempts: attempt });
  // eslint-disable-next-line no-use-before-define
  if (queued) scheduleBackgroundRetry();
  throw lastError;
}

let backgroundTimer = null;

/**
 * Keep retrying a queued lead while the page is open, with growing delays,
 * up to MAX_TOTAL_ATTEMPTS. Beyond that the queue still persists for the
 * next visit / online event.
 */
function scheduleBackgroundRetry() {
  if (typeof window === 'undefined' || backgroundTimer) return;
  const pending = readPending();
  if (!pending || pending.attempts >= MAX_TOTAL_ATTEMPTS) return;
  const idx = Math.max(0, pending.attempts - RETRY_DELAYS_MS.length - 1);
  const delay =
    BACKGROUND_RETRY_DELAYS_MS[
      Math.min(idx, BACKGROUND_RETRY_DELAYS_MS.length - 1)
    ];
  backgroundTimer = setTimeout(async () => {
    backgroundTimer = null;
    // eslint-disable-next-line no-use-before-define
    await flushPendingLead();
    if (readPending()) scheduleBackgroundRetry();
  }, delay);
}

function redirectToThanksIfOnForm() {
  try {
    if (formPaths.test(window.location.pathname)) {
      window.location.assign('/thanks');
    }
  } catch (e) {
    // ignore
  }
}

/**
 * Last-ditch delivery when the page is being hidden/closed with a lead still
 * queued: navigator.sendBeacon survives page unload and, with a text/plain
 * body, needs no CORS preflight. The API is idempotent so a later queue
 * flush cannot duplicate it.
 */
function beaconPendingLead() {
  if (!formApiEnabled) return;
  const pending = readPending();
  if (!pending || !pending.payload || !navigator.sendBeacon) return;
  try {
    navigator.sendBeacon(
      beaconBackendUrl,
      JSON.stringify({
        ...pending.payload,
        token: process.env.NEXT_PUBLIC_API_TOKEN,
      }),
    );
  } catch (e) {
    // ignore
  }
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : '';
}

function getStored(key) {
  try {
    return window.localStorage.getItem(key) || '';
  } catch (e) {
    return '';
  }
}

let lastPartialSnapshot = '';

/**
 * Progressive capture: send the values typed so far (debounced by the
 * PartialCapture component). text/plain body => no preflight; keepalive =>
 * survives navigation. Silent on failure: this is insurance, not the submit.
 */
export function sendPartial(values, page) {
  if (typeof window === 'undefined' || !values || !formApiEnabled) return;
  const interesting = [
    values.fullName,
    values.email,
    values.phoneNumber,
    values.postalCodeValue,
  ];
  if (!interesting.some(v => v && String(v).trim())) return;
  const body = {
    token: process.env.NEXT_PUBLIC_API_TOKEN,
    sessionUuid: getStored('sessionUuid'),
    page: page || '',
    fullName: values.fullName || '',
    email: values.email || '',
    phoneNumber: values.phoneNumber || '',
    postalCodeValue: values.postalCodeValue || '',
    city: values.city || '',
    windowsReplace: values.windowsReplace || '',
    windowsAge: values.windowsAge || '',
    next90days: values.next90days || '',
    utm_source: getStored('utm_source'),
    utm_medium: getStored('utm_medium'),
    utm_campaign: getStored('utm_campaign'),
    utm_term: getStored('utm_term'),
    utm_content: getStored('utm_content'),
    fbclid: getStored('fbclid'),
    fbp: getCookie('_fbp'),
    fbc: getCookie('_fbc'),
  };
  if (!body.sessionUuid) return;
  const snapshot = JSON.stringify(body);
  if (snapshot === lastPartialSnapshot) return;
  lastPartialSnapshot = snapshot;
  try {
    fetch(partialBackendUrl, {
      method: 'POST',
      body: snapshot,
      keepalive: true,
      mode: 'cors',
      credentials: 'omit',
    }).catch(() => {});
  } catch (e) {
    // ignore
  }
}

let flushing = false;

/**
 * Re-send a lead that was queued by a previous failed submission.
 * Safe to call repeatedly; the API is idempotent per sessionUuid for 24h.
 */
export async function flushPendingLead() {
  if (typeof window === 'undefined' || flushing || !formApiEnabled) return;
  const pending = readPending();
  if (!pending || !pending.payload) return;
  if (Date.now() - (pending.savedAt || 0) > MAX_PENDING_AGE_MS) {
    clearPending();
    return;
  }
  flushing = true;
  try {
    await postOnce(pending.payload);
    clearPending();
    rotateSessionUuid(pending.payload.sessionUuid);
    redirectToThanksIfOnForm();
  } catch (error) {
    if (isRetryable(error)) {
      writePending({ ...pending, attempts: (pending.attempts || 0) + 1 });
    } else {
      // Deterministic rejection (4xx): retrying will not help. The values
      // already reached the API through /partial, so stop retrying.
      clearPending();
    }
  } finally {
    flushing = false;
  }
}

/**
 * Flush the queue now and whenever the browser comes back online.
 * Returns a cleanup function for React effects.
 */
export function installPendingLeadFlusher() {
  if (typeof window === 'undefined') return () => {};
  flushPendingLead().then(() => {
    if (readPending()) scheduleBackgroundRetry();
  });
  const flush = () => {
    flushPendingLead();
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') flushPendingLead();
  };
  window.addEventListener('online', flush);
  window.addEventListener('pageshow', flush);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pagehide', beaconPendingLead);
  return () => {
    window.removeEventListener('online', flush);
    window.removeEventListener('pageshow', flush);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pagehide', beaconPendingLead);
    if (backgroundTimer) {
      clearTimeout(backgroundTimer);
      backgroundTimer = null;
    }
  };
}
